import { moveChildNodes } from '@utilities/theme';

/**
 * HeaderWrapper custom element that manages header behavior including:
 * - Fixed header positioning on scroll
 * - Mobile/desktop dropdown navigation
 * - Cart count updates from cart state changes
 * - Height calculations for CSS custom properties
 *
 * @class HeaderWrapper
 * @extends HTMLElement
 */
if (!customElements.get('header-wrapper')) {
  class HeaderWrapper extends HTMLElement {
    /**
     * Creates an instance of HeaderWrapper
     * Initializes properties and binds event handlers
     */
    constructor() {
      super();
      /** @type {boolean} Whether the header is in fixed position */
      this.isFixed = false;
      /** @type {HTMLElement|null} The announcement bar element */
      this.announcementBar = document.querySelector('.js-announcement-bar');
      /** @type {HTMLElement|null} The main wrapper element */
      this.wrapper = document.querySelector('.js-wrapper');
      /** @type {NodeList} All dropdown details elements */
      this.dropdowns = this.querySelectorAll('details:not([open]):not(.js-ignore)');
      /** @type {number} Tablet breakpoint in pixels */
      this.breakpointTablet = 989;
      /** @type {boolean} RAF scheduled flag to prevent multiple frames */
      this._rafScheduled = false;
      /** @type {Object} Cached height values to avoid unnecessary DOM updates */
      this._cachedHeights = { inner: 0, wrapper: 0 };
    }

    /**
     * Sets CSS custom properties for header heights used by internal components
     * (menu drawer, predictive search, logo sizing).
     *
     * Note: The total --header-height is handled by ResizeObserver in
     * layout-height-observer.js, which tracks #site-header dimensions automatically.
     * These internal variables are still needed for positioning elements WITHIN the
     * header structure.
     *
     * Batches DOM reads before writes to avoid layout thrashing (forced reflows).
     * Only updates CSS properties when values have actually changed.
     */
    setHeightProperty() {
      // Batch all DOM reads first (triggers single reflow)
      const headerEl = this.querySelector('header');
      const innerHeight = headerEl?.clientHeight ?? 0;
      const wrapperHeight = this.clientHeight;

      // Only write to DOM if values have changed (avoids unnecessary style recalcs)
      if (this._cachedHeights.inner !== innerHeight) {
        this._cachedHeights.inner = innerHeight;
        document.body.style.setProperty('--header-inner-height', `${innerHeight}px`);
      }
      if (this._cachedHeights.wrapper !== wrapperHeight) {
        this._cachedHeights.wrapper = wrapperHeight;
        document.body.style.setProperty('--header-wrapper-height', `${wrapperHeight}px`);
      }
    }

    /**
     * Toggles fixed positioning classes on header and announcement bar
     * Note: Height properties are set once on connectedCallback and don't need
     * recalculation when fixed state changes, as the header dimensions remain the same
     */
    toggleFixedClasses() {
      // Heights are stable - no recalculation needed when position changes
      // The CSS variables set in connectedCallback remain accurate
    }

    /**
     * Handles scroll events to determine if header should be fixed
     * Updates isFixed state based on scroll position
     * Uses requestAnimationFrame to batch updates and avoid layout thrashing
     */
    handleScrollEvent() {
      const wasFixed = this.isFixed;
      this.isFixed = window.scrollY > 0;

      // Only update if fixed state actually changed
      if (wasFixed !== this.isFixed) {
        this.toggleFixedClasses();
      }
    }

    /**
     * RAF-throttled scroll handler to prevent excessive reflows
     * Ensures only one layout calculation per animation frame
     */
    _throttledScrollHandler() {
      if (this._rafScheduled) return;
      this._rafScheduled = true;

      requestAnimationFrame(() => {
        this.handleScrollEvent();
        this._rafScheduled = false;
      });
    }

    /**
     * Initializes scroll event listener for sticky header behavior
     * Uses passive listener and RAF throttling for better performance
     * Calls handleScrollEvent immediately to set initial state
     */
    initOnScrollEvent() {
      // Use passive listener for better scroll performance
      window.addEventListener('scroll', () => this._throttledScrollHandler(), { passive: true });
      this.handleScrollEvent();
    }

    /**
     * Removes onclick attribute and handler from elements with js-remove-onclick class
     * Used for cleaning up inline event handlers
     */
    removeOnclick() {
      const button = this.querySelector('.js-remove-onclick');

      button.removeAttribute('onclick');
      button.onclick = null;
    }

    /**
     * Detects if the current device is iOS
     * @returns {boolean} True if running on iOS device
     */
    isIos() {
      if (typeof window === `undefined` || typeof navigator === `undefined`) return false;

        return /iPhone|iPad|iPod/i.test(navigator.userAgent || navigator.vendor || (window.opera && opera.toString() === `[object Opera]`));
    }

    /**
     * Called when the element is inserted into the DOM
     * Initializes all header functionality including:
     * - Height property calculations
     * - Cart state listeners
     * - Dropdown navigation behavior
     * - Mobile/desktop responsive behavior
     * - Sticky header initialization
     */
    connectedCallback() {
      this.setHeightProperty();

      // inject `header-drawer` HTML
      /**
       * Select all `.header__submenu` elements that have a [data-source] and [target-tab],
       * call moveChildNodes utility to move the child nodes from the source to the target.
       */
      const headerDrawerSubmenus = document.querySelectorAll('#header-group [data-source][data-target]:not([data-preview-mode="true"])');
      headerDrawerSubmenus.forEach((submenu) => {
        const source = submenu.getAttribute('data-source');
        const target = submenu.getAttribute('data-target');
        moveChildNodes(`[data-source="${source}"]`, `[data-target-menu="${target}"]`);
      });


      const anchors = this.querySelectorAll('a');
      const isIos = this.isIos();
      anchors.forEach(anchor => {
        const hasButton = anchor.querySelector('button');
        if(anchor.innerHTML.trim() == '' || hasButton) anchor.parentElement.removeChild(anchor);

        anchor.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      });

      if(window.innerWidth <= this.breakpointTablet) this.removeOnclick();

      /**
       * @param {HTMLDetailsElement} dropdown
       */
      const closeDesktopDropdown = (dropdown) => {
        const ul = dropdown.querySelector('summary + ul');
        if (ul) ul.style.maxHeight = '0rem';
        dropdown.removeAttribute('open');
        dropdown.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      };

      /**
       * @param {HTMLDetailsElement} dropdown
       */
      const openDesktopDropdown = (dropdown) => {
        dropdown.setAttribute('open', '');
        dropdown.querySelector('summary')?.setAttribute('aria-expanded', 'true');
        const ul = dropdown.querySelector('summary + ul');
        if (ul) ul.style.maxHeight = (ul.scrollHeight / 10) + 'rem';
      };

      this.dropdowns.forEach((dropdown) => {
        if(isIos) dropdown.style.maxHeight = 'auto';

        dropdown.addEventListener('mouseenter', (event) => {
          if(window.innerWidth <= this.breakpointTablet) return;
          openDesktopDropdown(dropdown);
        });

        dropdown.addEventListener('mouseleave', (event) => {
          if(window.innerWidth <= this.breakpointTablet) return;
          closeDesktopDropdown(dropdown);
        });

        // Keyboard parity (WCAG 2.1.1): open the submenu when a keyboard user
        // tabs into the dropdown and close it when focus leaves, mirroring the
        // hover behavior. Uses the same open/close helpers so maxHeight is set
        // correctly (setting only the `open` attribute leaves maxHeight at 0,
        // which makes the submenu flash open then collapse).
        dropdown.addEventListener('focusin', (event) => {
          if(window.innerWidth <= this.breakpointTablet) return;
          openDesktopDropdown(dropdown);
        });

        dropdown.addEventListener('focusout', (event) => {
          if(window.innerWidth <= this.breakpointTablet) return;
          // Only close if focus is moving outside this dropdown (e.g. tabbing
          // between options within the submenu should keep it open).
          if(dropdown.contains(event.relatedTarget)) return;
          closeDesktopDropdown(dropdown);
        });

        dropdown.addEventListener('click', (event) => {
          // Let the browser follow links on desktop. closest() also covers elements inside an anchor, such as the image tile button span.
          if(event.target.closest('a') && window.innerWidth >= this.breakpointTablet) return;

          event.preventDefault();
          event.stopPropagation();

          if(window.innerWidth > this.breakpointTablet) return;
          const parentDropdown = dropdown.closest('details:not(.js-ignore)');
          const grandparentDropdown = parentDropdown ? parentDropdown.closest('details:not(.js-ignore)') : null;

          if(!dropdown.hasAttribute('open')) {
            dropdown.setAttribute('open', '');
            const div = dropdown.querySelector('summary + div');
            const maxHeight = dropdown.getBoundingClientRect().height + div.getBoundingClientRect().height;
            dropdown.style.maxHeight = maxHeight > 0 ? maxHeight + 'px' : 'auto';
            let parentDropdownHeight = parentDropdown.clientHeight + maxHeight
            if(parentDropdown) {
              parentDropdown.setAttribute('data-original-height', parentDropdown.clientHeight);
              parentDropdown.style.maxHeight = maxHeight > 0 ? parentDropdownHeight + 'px' : 'auto';
            }
            if(grandparentDropdown && parentDropdown) {
              grandparentDropdown.setAttribute('data-original-height', grandparentDropdown.clientHeight + parentDropdownHeight);
              grandparentDropdown.style.maxHeight = maxHeight > 0 ? grandparentDropdown.clientHeight + parentDropdownHeight + maxHeight + 'px' : 'auto';
            }
          } else {
            const summary = dropdown.querySelector('summary');
            const summaryHeight = summary.getBoundingClientRect().height;
            dropdown.style.maxHeight = summaryHeight > 0 ? summaryHeight + 'px' : 'auto';
            if(parentDropdown.hasAttribute('data-original-height')) parentDropdown.style.maxHeight = parentDropdown.getAttribute('data-original-height') + 'px';
            if(grandparentDropdown.hasAttribute('data-original-height')) grandparentDropdown.style.maxHeight = grandparentDropdown.getAttribute('data-original-height') + 'px';
            this.timeout = window.setTimeout(() => {
              dropdown.removeAttribute('open');
              clearTimeout(this.timeout);
            }, 500);
          }
        });

        if(window.innerWidth > this.breakpointTablet) return;
        const dropSummary = dropdown.querySelector('summary');
        // dropdown.style.maxHeight = dropSummary.getBoundingClientRect().height + 'px';
      });

      const childLinks = this.querySelectorAll('.js-header-childlink');

      childLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          if(window.innerWidth <= this.breakpointTablet) return;
          if(!event.target.classList.contains('js-header-childlink')) return;

          event.preventDefault();
        });
      });

      const summaryAnchors = this.querySelectorAll('summary > a');

      summaryAnchors.forEach((anchor) => {
        anchor.addEventListener('click', (event) => {
          if(window.innerWidth > this.breakpointTablet) return;

          // Toggle directly. A synthetic click on the summary reaches analytics pixels as a user click.
          const details = anchor.closest('details');
          if (details) details.open = !details.open;
        });
      });

      if(!this.classList.contains('js-sticky')) return;
      this.initOnScrollEvent();
    }
  }

  customElements.define('header-wrapper', HeaderWrapper);
}
